import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CompanyContext = createContext(null);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentCompany, setCurrentCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch companies list (only for super admins)
  const fetchCompanies = useCallback(async () => {
    if (!user?.is_super_admin) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
      
      // Set default company from user's company_id or first in list
      const savedCompanyId = localStorage.getItem('superAdminSelectedCompany');
      const userCompanyId = user?.company_id;
      
      if (savedCompanyId) {
        const savedCompany = response.data.companies.find(c => c.id === savedCompanyId);
        if (savedCompany) {
          setCurrentCompany(savedCompany);
        } else if (response.data.companies.length > 0) {
          setCurrentCompany(response.data.companies[0]);
        }
      } else if (userCompanyId) {
        const userCompany = response.data.companies.find(c => c.id === userCompanyId);
        if (userCompany) {
          setCurrentCompany(userCompany);
        } else if (response.data.companies.length > 0) {
          setCurrentCompany(response.data.companies[0]);
        }
      } else if (response.data.companies.length > 0) {
        setCurrentCompany(response.data.companies[0]);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchCompanies();
    } else {
      setLoading(false);
    }
  }, [user, fetchCompanies]);

  // Switch to a different company (Super Admin only)
  const switchCompany = useCallback((company) => {
    if (!user?.is_super_admin) return;
    
    setCurrentCompany(company);
    localStorage.setItem('superAdminSelectedCompany', company.id);
  }, [user]);

  // Get the effective company ID (for API calls)
  const getEffectiveCompanyId = useCallback(() => {
    if (user?.is_super_admin && currentCompany) {
      return currentCompany.id;
    }
    return user?.company_id || null;
  }, [user, currentCompany]);

  // Refresh companies list
  const refreshCompanies = useCallback(() => {
    return fetchCompanies();
  }, [fetchCompanies]);

  return (
    <CompanyContext.Provider value={{
      currentCompany,
      companies,
      loading,
      switchCompany,
      getEffectiveCompanyId,
      refreshCompanies,
      isSuperAdmin: user?.is_super_admin || false
    }}>
      {children}
    </CompanyContext.Provider>
  );
};
